import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Send, Sparkles, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useVisualViewport, useIsMobile } from "../lib/useVisualViewport";

const SUGGESTIONS = {
  candidate: ["Help me improve my profile", "What should I say in an interview?", "Which jobs suit me?"],
  employer: ["How are my listings doing?", "Help me write a job advert", "How do interviews work here?"],
  admin: ["Give me a platform summary"],
  visitor: ["How does HiredToday work?", "Is it free for jobseekers?", "How do I post a job?"],
};

export default function ChatAssistant({ user, toggleTestId = "chat-assistant-toggle" }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState("");
  const endRef = useRef(null);
  const openPanelRef = useRef(null);
  // pin the panel to the VISIBLE viewport while the on-screen keyboard is open —
  // iOS shrinks the visual viewport without shrinking 100dvh, which used to hide the
  // composer behind the keyboard and leave the page zoomed/shifted afterwards
  const isMobile = useIsMobile();
  const vv = useVisualViewport(open && isMobile);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  useEffect(() => {
    const onOpen = () => openPanelRef.current?.();
    window.addEventListener("ht:open-assistant", onOpen);
    return () => window.removeEventListener("ht:open-assistant", onOpen);
  }, []);

  const role = !user ? "visitor" : (user.role === "employer" || user.role === "admin" ? user.role : "candidate");
  const suggestions = SUGGESTIONS[role] || SUGGESTIONS.candidate;

  const openPanel = () => {
    setOpen(true);
    if (messages.length === 0) {
      const hello = role === "visitor"
        ? "Hi, I'm the HiredToday assistant. Ask me how the platform works, what it costs or how to get started, jobseekers are always free here."
        : role === "candidate"
        ? `Hi ${user.name?.split(" ")[0] || "there"}, I'm the HiredToday assistant. Ask me anything about your job search, your profile, applications or interviews.`
        : `Hi ${user.name?.split(" ")[0] || "there"}, I'm the HiredToday assistant. I can help with your listings, adverts, applicants, interviews, offers and anything else on the platform.`;
      setMessages([{ from: "ai", text: hello }]);
    }
  };
  openPanelRef.current = openPanel;

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { from: "me", text: msg }]);
    setBusy(true);
    try {
      const r = await api.post("/ai/chat", { message: msg, session_id: sessionId });
      if (r.data.session_id) setSessionId(r.data.session_id);
      setMessages((m) => [...m, { from: "ai", text: r.data.reply }]);
    } catch (err) {
      toast.error(formatApiError(err));
      setMessages((m) => [...m, { from: "ai", text: "Sorry, I hit a problem just then. Try asking me again." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button onClick={() => (open ? setOpen(false) : openPanel())} data-testid={toggleTestId}
        aria-label="HiredToday assistant"
        className="relative p-2 rounded-full text-white bg-gradient-to-br from-violet-500 via-purple-600 to-fuchsia-600 shadow-md shadow-purple-500/25 hover:shadow-lg hover:shadow-purple-500/40 hover:scale-110 hover:-translate-y-0.5 active:scale-95 transition-[transform,box-shadow] duration-200">
        <Sparkles className="h-4 w-4" />
      </button>
      {open && createPortal(
        <div data-testid="chat-assistant-panel"
          style={vv ? { height: vv.height, top: vv.top, bottom: "auto" } : undefined}
          className="fixed inset-0 h-[100dvh] sm:inset-auto sm:bottom-20 sm:right-6 sm:w-96 sm:h-[560px] z-[90] bg-white sm:border sm:border-zinc-200 sm:rounded-lg shadow-2xl flex flex-col overflow-hidden overscroll-contain">
          <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-500 text-white shrink-0">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <p className="text-sm font-semibold">HiredToday assistant</p>
            </div>
            <button onClick={() => setOpen(false)} data-testid="chat-assistant-close" aria-label="Close assistant"
              className="p-1 hover:bg-white/20 rounded transition-colors">
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div key={`msg-${i}`} className={`flex ${m.from === "me" ? "justify-end" : "justify-start"}`}>
                <p className={`max-w-[85%] px-3.5 py-2.5 rounded-lg text-sm leading-relaxed whitespace-pre-line ${
                  m.from === "me" ? "bg-gradient-to-br from-violet-600 to-purple-600 text-white" : "bg-zinc-100 text-zinc-800"}`}
                  data-testid={`chat-msg-${i}`}>
                  {m.text}
                </p>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <p className="bg-zinc-100 text-zinc-400 px-3.5 py-2.5 rounded-lg text-sm" data-testid="chat-thinking">Thinking…</p>
              </div>
            )}
            {messages.length <= 1 && !busy && (
              <div className="flex flex-wrap gap-2 pt-1">
                {suggestions.map((s) => (
                  <button key={s} onClick={() => send(s)} data-testid={`chat-suggestion-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="text-xs font-medium text-violet-700 bg-violet-50 border border-violet-200 rounded-full px-3 py-1.5 hover:bg-violet-100 transition-colors">
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-center gap-2 px-3 py-3 border-t border-zinc-100 shrink-0 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <input value={input} onChange={(e) => setInput(e.target.value)} data-testid="chat-input"
              placeholder="Ask me anything…" maxLength={1000}
              className="flex-1 h-10 px-3 border border-zinc-300 rounded-md text-base focus:outline-none focus:ring-2 focus:ring-violet-600" />
            <button type="submit" disabled={busy || !input.trim()} data-testid="chat-send" aria-label="Send message"
              className="h-10 w-10 inline-flex items-center justify-center bg-gradient-to-br from-violet-600 to-purple-600 text-white rounded-md hover:shadow-md hover:shadow-purple-500/30 hover:scale-105 active:scale-95 disabled:opacity-40 transition-[transform,box-shadow] duration-200">
              <Send className="h-4 w-4" />
            </button>
          </form>
        </div>,
        document.body
      )}
    </>
  );
}
